import { useState } from "react";
import styled from "styled-components/macro";
import {
  Box,
  BoxContent,
  BoxHeader,
  BoxNavRight,
  BoxNavButton,
  BoxFooter,
  BoxNavLeft,
} from "./Box";
import { SkipioLogo, Close } from "./Icons";
import Button from "./Button";
import Stack from "./Stack";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import Title from "./Title";
import { Menu, MenuLinkItem, MenuSeparator } from "./Menu";
import { Link } from "react-router-dom";
import { PostViewerSidePanel } from "./PostViewer";
import useRootHistory from "../hooks/use-root-history";
import { useQuery } from "@tanstack/react-query";
import { fetchUser } from "../api/users";

export default function HelpButton({
  trigger,
  ...props
}: {
  trigger: SidePanelTrigger;
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <>
          <BoxHeader stacked>
            <BoxNavRight>
              <BoxNavButton onClick={hide}>
                <Close />
              </BoxNavButton>
            </BoxNavRight>
          </BoxHeader>
          <BoxContent dark>
            <Stack space={20}>
              <Center>
                <Logo />
              </Center>
              <SupportTicket onHide={hide} {...props} />
            </Stack>
          </BoxContent>
        </>
      )}
    />
  );
}

export function SupportTicket({
  onHide = () => (window.location.href = window.location.pathname),
  title,
  subtitle,
  initialState = "initial",
}: {
  onHide?: () => void;
  title?: string;
  subtitle?: string;
  initialState?: "initial" | "ticket";
}) {
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    suspense: false,
    select: (queryData) => queryData.data,
  }).data;

  const [state, setState] = useState(initialState);

  const rootHistory = useRootHistory();

  return (
    <>
      {
        {
          initial: (
            <Box elevated>
              <BoxContent>
                <Stack>
                  <Title>
                    We're here to ensure you succeed with Skipio!
                    <small>
                      Here's how to get help and get your questions answered as
                      quickly as possible.{" "}
                    </small>
                  </Title>
                  <Menu>
                    {Boolean(user) && (
                      <>
                        <PostViewerSidePanel
                          articleId="d220c78e-da8a-4d4b-ae81-56f07de604e6"
                          trigger={({ ref, show }) => (
                            <MenuItem to="#" as={Link} ref={ref} onClick={show}>
                              <Title>
                                Check out the Skipio Starter Guide for help
                                setting up your account.
                                <small>View the Starter Guide here.</small>
                              </Title>
                            </MenuItem>
                          )}
                        />
                        <MenuSeparator />
                        <MenuItem
                          to="/app/resource-center"
                          onClick={() => {
                            rootHistory.push("/app/resource-center");
                            onHide();
                          }}
                        >
                          <Title>
                            Enjoy step-by-step tutorials, training videos, and
                            how-to articles.
                            <small>Visit the Resource Center here.</small>
                          </Title>
                        </MenuItem>
                        <MenuSeparator />
                      </>
                    )}
                    <MenuItem
                      to="#"
                      as={Link}
                      onClick={() => setState("ticket")}
                    >
                      <Title>
                        Still need help?
                        <small>
                          Submit a detailed description of your problem.
                        </small>
                      </Title>
                    </MenuItem>
                  </Menu>
                </Stack>
              </BoxContent>
              <BoxFooter>
                <BoxNavLeft>
                  <Button variant="link" onClick={onHide}>
                    Cancel
                  </Button>
                </BoxNavLeft>
              </BoxFooter>
            </Box>
          ),
          ticket: (
            <Box elevated>
              <BoxContent>
                <Stack>
                  <Title>
                    {title ? title : "Get in contact with us"}
                    <small>
                      {subtitle ? (
                        subtitle
                      ) : (
                        <>
                          Email{" "}
                          <a href="mailto:support@skipio.com">
                            support@skipio.com
                          </a>{" "}
                          with your questions. Please include your first name,
                          last name, and email address associated with your
                          Skipio account.
                        </>
                      )}
                    </small>
                  </Title>
                </Stack>
              </BoxContent>
              <BoxFooter white stacked>
                <BoxNavLeft>
                  <Button
                    variant="link"
                    onClick={() =>
                      initialState === "initial"
                        ? setState("initial")
                        : onHide()
                    }
                  >
                    Cancel
                  </Button>
                </BoxNavLeft>
              </BoxFooter>
            </Box>
          ),
        }[state]
      }
    </>
  );
}

const Center = styled.div`
  text-align: center;
`;

const Logo = styled(SkipioLogo)`
  width: 120px;
  height: 120px;
  color: #888;
  stroke: black;
  stroke-width: 0.2px;
  opacity: 0.1;
`;

const MenuItem = styled(MenuLinkItem)`
  padding-top: 10px;
  padding-bottom: 10px;
  white-space: unset;
`;
